import { useQuery } from '@tanstack/react-query';
import { uniqBy } from 'lodash';

import { useAppContext } from '@app/contexts';
import { useFeatureFlag } from '@app/hooks';
import { makeRequest } from '@app/utils';
import { Feature } from '@app/constants';

export interface ServicePointsDto {
  allowedServicePoints: {
    id: string;
    name: string;
    libraryName?: string;
    libraryId?: string;
  }[];
  isPickupAnywhere?: boolean;
  homeLibraryId?: string;
}

interface UseAllowedServicePointsFetchProps {
  id: string;
  shouldFetch: boolean;
}

export const useAllowedServicePointsFetch = ({
  id,
  shouldFetch,
}: UseAllowedServicePointsFetchProps) => {
  const { isLoggedIn, patronSession } = useAppContext();
  const { isActive: isConsortiaLogin } = useFeatureFlag({
    name: Feature.consortiaLoginFlow,
  });

  return useQuery(
    ['allowedLocations', id],
    () =>
      makeRequest<ServicePointsDto>({
        url: `/opac-patron/account/${patronSession?.user?.externalSystemId}/instance/${id}/allowed-service-points`,
        withCredentials: isConsortiaLogin,
      }),
    {
      enabled: isLoggedIn && shouldFetch,
      select: data => {
        const allowedServicePoints = data.allowedServicePoints ?? [];
        const libraries = (
          (data.isPickupAnywhere &&
            uniqBy(allowedServicePoints, 'libraryId')) ||
          []
        )
          .map(({ libraryId, libraryName }) => ({
            label: libraryName as string,
            id: libraryId as string,
          }))
          .sort((library1, library2) =>
            library1.label.localeCompare(library2.label)
          );

        return {
          ...data,
          allowedServicePoints: allowedServicePoints
            .map(({ id, name, libraryId }) => ({ label: name, id, libraryId }))
            .sort((servicePoint1, servicePoint2) =>
              servicePoint1.label.localeCompare(servicePoint2.label)
            ),
          libraries,
        };
      },
    }
  );
};
